import {
  fetchMembers,
  fetchMemberships,
  fetchAttendance,
  fetchTrainers,
  fetchEquipments,
  fetchLockers,
  fetchClasses,
  fetchPayments,
  fetchFreezes,
} from './receptionistApi';
import { cachedQuery, invalidateCache } from '../cache/queryCache';
import { getCachedGymId } from './gymContext';
import { branchScopeCacheSuffix, getReceptionistBranchFilter, isReceptionistPanelScope } from '../gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';
import { emptyDashboardOverview, fetchDashboardOverviewRpc } from '../shared/dashboardRpcApi';
import { RECEPTIONIST_SALE_PAGES } from '../../pages/receptionist/sales/salePageConfig';
import { saleRowLeft } from '../../pages/receptionist/sales/salePaymentUtils';
import {
  computeDashboardSaleStats,
  dashboardPaymentAmount,
  dashboardPaymentDay,
  dashboardTodayKey,
  isDashboardGuestPassPayment,
  isDashboardNewMembershipPayment,
  isDashboardRenewalPayment,
} from '../shared/dashboardSaleMetrics';

function todayKey() {
  return dashboardTodayKey();
}

function last7Days() {
  const days = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${day}`);
  }
  return days;
}

function daysBetween(start, end) {
  const days = [];
  if (!start || !end || end < start) return days;
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  const maxDays = 62;
  let n = 0;
  while (cursor <= last && n < maxDays) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
    n += 1;
  }
  return days;
}

function resolveChartDays(startDate, endDate) {
  if (startDate && endDate) {
    const range = daysBetween(startDate, endDate);
    if (range.length) return range;
  }
  if (startDate && !endDate) return daysBetween(startDate, todayKey());
  if (!startDate && endDate) {
    const start = new Date(`${endDate}T00:00:00`);
    start.setDate(start.getDate() - 6);
    return daysBetween(start.toISOString().slice(0, 10), endDate);
  }
  return last7Days();
}

function chartLabels(days) {
  if (days.length > 14) {
    return days.map((d) =>
      new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    );
  }
  return days.map((d) =>
    new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' }),
  );
}

function inDateRange(value, startDate, endDate) {
  const d = String(value || '').slice(0, 10);
  if (!d) return false;
  if (startDate && d < startDate) return false;
  if (endDate && d > endDate) return false;
  return true;
}

function hasDateFilter(startDate, endDate) {
  return Boolean(startDate || endDate);
}

function paymentDay(p) {
  return dashboardPaymentDay(p);
}

function countByDay(items, dateKey, days) {
  return days.map((day) => items.filter((item) => (item[dateKey] || '').slice(0, 10) === day).length);
}

function sumByDay(items, dateKey, amountKey, days) {
  return days.map((day) =>
    items
      .filter((item) => (item[dateKey] || '').slice(0, 10) === day)
      .reduce((sum, item) => sum + (Number(item[amountKey]) || 0), 0),
  );
}

/**
 * @param {{ startDate?: string, endDate?: string }} [range]
 */
export async function fetchDashboardOverview(range = {}) {
  const today = pkTodayKey();
  const lockToday = isReceptionistPanelScope();
  const startDate = lockToday ? today : (range.startDate || '');
  const endDate = lockToday ? today : (range.endDate || '');
  const filtered = hasDateFilter(startDate, endDate);
  const gid = getCachedGymId() || 'gym';
  const cacheKey = `gym:${gid}:receptionist-dashboard-rpc-v1${branchScopeCacheSuffix()}:${startDate || 'all'}:${endDate || 'all'}`;
  const branch = String(getReceptionistBranchFilter() || '').trim();

  invalidateCache(`gym:${gid}:receptionist-dashboard-`);

  return cachedQuery(cacheKey, async () => {
    if (!branch) {
      return emptyDashboardOverview('This branch');
    }
    try {
      const rpc = await fetchDashboardOverviewRpc({
        startDate,
        endDate,
        branch,
        panel: 'receptionist',
        scopeLabel: 'This branch',
      });
      if (rpc) return rpc;
    } catch (err) {
      console.warn('Receptionist dashboard RPC failed, using fallback', err?.message || err);
    }
    return fetchDashboardOverviewLegacy({ startDate, endDate }, { filtered, startDate, endDate });
  }, 15_000);
}

async function fetchDashboardOverviewLegacy(range, ctx) {
  const startDate = ctx.startDate;
  const endDate = ctx.endDate;
  const filtered = ctx.filtered;
  return (async () => {
    const [
      members,
      memberships,
      attendance,
      trainers,
      equipments,
      lockers,
      classes,
      payments,
      freezes,
    ] = await Promise.all([
      fetchMembers(),
      fetchMemberships(),
      fetchAttendance(),
      fetchTrainers(),
      fetchEquipments(),
      fetchLockers(),
      fetchClasses(),
      fetchPayments(),
      fetchFreezes(),
    ]);

    const today = todayKey();
    const useDays = resolveChartDays(startDate, endDate);

    const activeMemberships = memberships.filter((m) => m.status === 'active' && !m.frozen);
    const assignedLockers = lockers.filter((l) => l.status === 'assigned');
    const activeFreezes = freezes.filter((f) => String(f.status || '').toLowerCase() === 'active');

    const membersInRange = filtered
      ? members.filter((m) => inDateRange(m.createdAt || m.joiningDate, startDate, endDate))
      : members;
    const attendanceInRange = filtered
      ? attendance.filter((a) => inDateRange(a.date, startDate, endDate))
      : attendance.filter((a) => a.date === today);
    const freezesInRange = filtered
      ? freezes.filter((f) => inDateRange(f.startDate || f.createdAt, startDate, endDate))
      : activeFreezes;

    const saleMetrics = computeDashboardSaleStats(payments, {
      startDate,
      endDate,
      filtered,
      scopeHint: 'This branch',
    });

    const pendingPayments = payments.filter((p) => saleRowLeft(p) > 0);
    const scopeHint = 'This branch';

    const stats = [
      {
        key: 'members',
        label: filtered ? 'New Members (range)' : 'Total Members',
        value: String(membersInRange.length),
        change: filtered ? scopeHint : `${scopeHint} · all-time`,
        variant: 'primary',
      },
      saleMetrics.newMembershipStat,
      saleMetrics.newMembershipAmountStat,
      saleMetrics.registrationFeeStat,
      {
        key: 'memberships',
        label: 'Active Memberships',
        value: String(activeMemberships.length),
        change: scopeHint,
        variant: 'success',
      },
      {
        key: 'attendance',
        label: filtered ? 'Attendance (range)' : "Today's Attendance",
        value: String(attendanceInRange.length),
        change: filtered ? scopeHint : `${scopeHint} · today`,
        variant: 'warning',
      },
      {
        key: 'classes',
        label: 'Classes',
        value: String(classes.length),
        change: 'Gym-wide',
        variant: 'primary',
      },
      saleMetrics.guestPassStat,
      ...saleMetrics.saleStats,
      {
        key: 'remaining',
        label: 'Remaining Payments (open)',
        value: String(pendingPayments.length),
        change: `${scopeHint} · outstanding balance`,
        variant: 'warning',
      },
      {
        key: 'freeze',
        label: filtered ? 'Freezes (range)' : 'Active Freezes',
        value: String(freezesInRange.length),
        change: filtered ? scopeHint : `${scopeHint} · active now`,
        variant: 'info',
      },
      saleMetrics.revenueStat,
      saleMetrics.paymentsCountStat,
      {
        key: 'trainers',
        label: 'Trainers',
        value: String(trainers.filter((t) => t.status === 'active').length),
        change: `${scopeHint} · active`,
        variant: 'success',
      },
      {
        key: 'equipments',
        label: 'Equipment',
        value: String(equipments.length),
        change: 'Gym-wide',
        variant: 'warning',
      },
      {
        key: 'lockers',
        label: 'Assigned Lockers',
        value: String(assignedLockers.length),
        change: `${scopeHint} · assigned`,
        variant: 'primary',
      },
    ];

    const chartPayments = filtered
      ? payments.filter((p) => inDateRange(paymentDay(p), startDate, endDate))
      : payments.filter((p) => {
        const d = paymentDay(p);
        return d && useDays.includes(d);
      });
    const chartMembers = filtered
      ? members.filter((m) => inDateRange(m.createdAt || m.joiningDate, startDate, endDate))
      : members;
    const chartSubs = chartPayments.filter((p) => isDashboardNewMembershipPayment(p));
    const chartRenewals = chartPayments.filter((p) => isDashboardRenewalPayment(p));

    const categorySlices = [];
    const newMemAmt = chartPayments
      .filter((p) => isDashboardNewMembershipPayment(p))
      .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
    if (newMemAmt > 0 || saleMetrics.newMembershipCount > 0) {
      categorySlices.push({ label: 'New Membership', amount: newMemAmt });
    }
    RECEPTIONIST_SALE_PAGES.forEach((page) => {
      const amount = chartPayments
        .filter((p) => String(p.source || '').toLowerCase() === page.source)
        .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
      if (amount > 0) categorySlices.push({ label: page.label, amount });
    });
    const guestAmt = chartPayments
      .filter((p) => isDashboardGuestPassPayment(p))
      .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
    if (guestAmt > 0) categorySlices.push({ label: 'Guest Pass', amount: guestAmt });

    const charts = {
      dailyCollection: {
        labels: chartLabels(useDays),
        values: sumByDay(
          chartPayments.map((p) => ({ ...p, date: paymentDay(p), amount: dashboardPaymentAmount(p) })),
          'date',
          'amount',
          useDays,
        ),
      },
      salesByDay: {
        labels: chartLabels(useDays),
        values: sumByDay(
          chartPayments.map((p) => ({ ...p, date: paymentDay(p), amount: dashboardPaymentAmount(p) })),
          'date',
          'amount',
          useDays,
        ),
      },
      categorySales: {
        labels: categorySlices.map((s) => s.label),
        values: categorySlices.map((s) => s.amount),
      },
      membershipVsRenewal: {
        labels: ['New Membership', 'Renewal'],
        values: [
          chartSubs.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0),
          chartRenewals.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0),
        ],
      },
      subscriptionTrend: {
        labels: chartLabels(useDays),
        values: countByDay(
          chartSubs.map((p) => ({ date: paymentDay(p) })),
          'date',
          useDays,
        ),
      },
      registrationTrend: {
        labels: chartLabels(useDays),
        values: countByDay(
          chartMembers.map((m) => ({ date: String(m.createdAt || m.joiningDate || '').slice(0, 10) })),
          'date',
          useDays,
        ),
      },
    };

    return { stats, charts };
  })();
}
